import PropTypes from 'prop-types'
import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import api from 'api'
import {
  useLazyGetAccountDetailsVersionsQuery,
  useLazyGetTagRefsVersionsQuery,
  useLazyGetAddressVersionsByIdQuery,
  useLazyGetContactVersionReferencesByAccountIdQuery,
  useLazyGetBillingProfileVersionsQuery,
  useLazyGetLateFeeVersionsByAccountIdQuery,
  useLazyGetLifecycleEventVersionsByAccountIdQuery,
} from 'api/entity-version/entityVersionCrud'
import { useLazyGetGeneralBillingQuery } from 'api/settings/getGeneralBilling'
import { getPaymentTermsOptionText } from 'data/customer/billingDetailsSelector'
import { getAccountMeta } from 'data/meta/accountMetaSelectors'

import { buildAccountActivityRows } from './accountActivityRowsUtils'

const AccountActivityContext = createContext(null)

export const useAccountActivity = () => {
  const ctx = useContext(AccountActivityContext)
  if (!ctx) throw new Error('useAccountActivity must be used within AccountActivityProvider')
  return ctx
}

const AccountActivityProvider = ({ accountId, billingAddressId, children }) => {
  const [getVersions, { data: accountData, isLoading: isAccountLoading }] = useLazyGetAccountDetailsVersionsQuery()
  const [getAddressVersionsById, { data: addressData, isLoading: isAddressLoading }] = useLazyGetAddressVersionsByIdQuery()
  const [getContactRefs, { data: contactRefsData, isLoading: isContactRefsLoading }] = useLazyGetContactVersionReferencesByAccountIdQuery()
  const [getGeneralBilling, { data: generalBillingData }] = useLazyGetGeneralBillingQuery()
  const [getTagRefsVersions, { data: accountTagRefsData, isLoading: isAccountTagRefsLoading }] = useLazyGetTagRefsVersionsQuery()
  const [getBillingProfileVersions, { data: billingProfileVersionsData, isLoading: isBillingProfileVersionsLoading }] =
    useLazyGetBillingProfileVersionsQuery()
  const [getLateFeeVersions, { data: accountLateFeeVersionsData, isLoading: isLateFeeVersionsLoading }] =
    useLazyGetLateFeeVersionsByAccountIdQuery()
  const [getLifecycleEventVersions, { data: accountLifecycleEventVersionsData, isLoading: isLifecycleEventVersionsLoading }] =
    useLazyGetLifecycleEventVersionsByAccountIdQuery()

  const dispatch = useDispatch()

  const customerMeta = useSelector(getAccountMeta, shallowEqual)
  const csrs = customerMeta?.csrs || []

  useEffect(() => {
    if (accountId) getVersions({ accountId })
  }, [accountId, getVersions])

  // Fetch payment terms dictionary once for display mapping
  useEffect(() => {
    getGeneralBilling()
  }, [getGeneralBilling])

  // Fetch contact reference ids for the account
  useEffect(() => {
    if (accountId) getContactRefs({ accountId })
  }, [accountId, getContactRefs])

  useEffect(() => {
    if (accountId) getTagRefsVersions({ id: accountId })
  }, [accountId, getTagRefsVersions])

  // Fetch billing address versions when id is available
  useEffect(() => {
    if (billingAddressId) getAddressVersionsById({ addressId: billingAddressId })
  }, [billingAddressId, getAddressVersionsById])

  useEffect(() => {
    if (accountId) getBillingProfileVersions({ accountId })
  }, [accountId, getBillingProfileVersions])

  useEffect(() => {
    if (accountId) getLateFeeVersions({ accountId })
  }, [accountId, getLateFeeVersions])

  useEffect(() => {
    if (accountId) getLifecycleEventVersions({ accountId })
  }, [accountId, getLifecycleEventVersions])

  // Subscribe to each contact's versions so cache invalidation triggers automatic refetches
  const contactIds = useMemo(() => {
    const refsObj = contactRefsData?.contactRefs
    return refsObj && typeof refsObj === 'object' ? Object.keys(refsObj) : []
  }, [contactRefsData])

  useEffect(() => {
    if (!contactIds.length) return
    // Start subscriptions for all contacts
    const subs = contactIds.map(contactId => dispatch(api.endpoints.getContactVersionsById.initiate({ contactId }, { subscribe: true })))
    // Cleanup subscriptions on ids change/unmount
    return () => {
      subs.forEach(sub => sub?.unsubscribe && sub.unsubscribe())
    }
  }, [dispatch, contactIds])

  // Derive contact versions and loading state from RTK Query cache
  const { contactVersionsById, isContactVersionsLoading } = useSelector(state => {
    const map = {}
    let loading = false
    for (const contactId of contactIds) {
      const selectContact = api.endpoints.getContactVersionsById.select({ contactId })
      const q = selectContact(state)
      const versions = Array.isArray(q?.data?.versions) ? q.data.versions : []
      map[contactId] = versions
      if (!q || q.status === 'uninitialized' || q?.isLoading || q?.isFetching) loading = true
    }
    return { contactVersionsById: map, isContactVersionsLoading: loading }
  }, shallowEqual)

  const paymentTermsMap = useMemo(() => {
    const map = new Map()
    const list = Array.isArray(generalBillingData?.paymentTerm) ? generalBillingData.paymentTerm : []
    for (const item of list) {
      const id = item?.id
      if (!id) continue
      const label = getPaymentTermsOptionText(item?.term, item?.interval)
      map.set(String(id), label)
    }
    return map
  }, [generalBillingData])

  const treeData = useMemo(() => {
    let versions = []
    if (Array.isArray(accountData)) {
      versions = accountData
    } else if (Array.isArray(accountData?.versions)) {
      versions = accountData.versions
    }

    let addressVersions = []
    if (Array.isArray(addressData)) {
      addressVersions = addressData
    } else if (Array.isArray(addressData?.versions)) {
      addressVersions = addressData.versions
    }

    let billingProfileVersions = []
    if (Array.isArray(billingProfileVersionsData)) {
      billingProfileVersions = billingProfileVersionsData
    } else if (Array.isArray(billingProfileVersionsData?.versions)) {
      billingProfileVersions = billingProfileVersionsData.versions
    }

    return buildAccountActivityRows(versions, addressVersions, {
      fixedAddressId: billingAddressId,
      csrs,
      contactVersionsById,
      paymentTermsMap,
      accountTagRefsData,
      billingProfileVersions,
      accountLateFeeVersionsData,
      accountLifecycleEventVersionsData,
    })
  }, [
    accountData,
    addressData,
    billingAddressId,
    csrs,
    contactVersionsById,
    paymentTermsMap,
    billingProfileVersionsData,
    accountLateFeeVersionsData,
    accountLifecycleEventVersionsData,
    accountTagRefsData,
  ])

  const isLoading =
    isAccountLoading ||
    (billingAddressId ? isAddressLoading : false) ||
    isContactRefsLoading ||
    isContactVersionsLoading ||
    isAccountTagRefsLoading ||
    isBillingProfileVersionsLoading ||
    isLateFeeVersionsLoading ||
    isLifecycleEventVersionsLoading

  const value = useMemo(() => ({ treeData, isLoading }), [treeData, isLoading])

  return <AccountActivityContext.Provider value={value}>{children}</AccountActivityContext.Provider>
}

AccountActivityProvider.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  billingAddressId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node.isRequired,
}

export default AccountActivityProvider
